import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Tag, Spin, Breadcrumb, Button, List, Divider, Rate, Input, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeftOutlined, 
  EyeOutlined, 
  ClockCircleOutlined, 
  UserOutlined,
  ShareAltOutlined,
  QuestionCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { CACHE_KEYS } from '../../store/queryClient';
import { getArticleById, getArticleByIdWithLocale, getFolderArticles, getArticlesInFolder, getAllFaqData, submitArticleFeedback, getArticleFeedback } from '../../services/freshdeskService';
import styles from './ArticleDetail.module.css';

const { Title, Paragraph } = Typography;

const ArticleDetail = () => {
  const { t, i18n } = useTranslation();
  const { id, slug } = useParams();
  
  // URL'den article ID'yi çıkar (SEO dostu URL'den)
  const articleId = id?.split('-')[0];
  const [searchTerm, setSearchTerm] = useState('');

  // React Query ile makale verisi çek - Yeni optimize edilmiş endpoint ile
  const { 
    data: articleData, 
    isLoading: isArticleLoading, 
    isError: isArticleError 
  } = useQuery({
    queryKey: CACHE_KEYS.ARTICLE_WITH_LOCALE(articleId, i18n.language),
    queryFn: () => getArticleByIdWithLocale(articleId, i18n.language),
    enabled: !!articleId,
    staleTime: 10 * 60 * 1000, // 10 dakika
    gcTime: 20 * 60 * 1000, // 20 dakika
  });

  // Makale verisini işle - Yeni endpoint yapısına göre
  const article = articleData?.data || articleData;

  // Tüm FAQ verisini çek (kategori bilgisi için)
  const { 
    data: allFaqData 
  } = useQuery({
    queryKey: CACHE_KEYS.ALL_FAQ_DATA(i18n.language),
    queryFn: () => getAllFaqData(i18n.language),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Folder bilgisini çek (makale verisinden folder_id alındıktan sonra)
  const { 
    data: folderData 
  } = useQuery({
    queryKey: CACHE_KEYS.FOLDER_ARTICLES(article?.folder_id, i18n.language),
    queryFn: () => getFolderArticles(article?.folder_id, i18n.language),
    enabled: !!article?.folder_id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });



  // Kategori ve folder bilgisini bul
  const categories = allFaqData?.data?.categories || [];
  let categoryName = 'Uncategorized';
  let folderId = null;
  
  if (article && categories.length > 0) {
    for (const category of categories) {
      if (category.folders && Array.isArray(category.folders)) {
        for (const folder of category.folders) {
          if (folder.articles && Array.isArray(folder.articles)) {
            const foundArticle = folder.articles.find(art => art.id === parseInt(articleId));
            if (foundArticle) {
              categoryName = category.name;
              folderId = folder.id;
              break;
            }
          }
        }
        if (categoryName !== 'Uncategorized') break;
      }
    }
  }

  // İşlenmiş makale verisi
  const processedArticle = useMemo(() => {
    if (!article) return null;
    
    // Folder bilgisini folderData'dan al
    const folderName = folderData?.data?.folder?.name || categoryName;
    const folderId = article.folder_id || article.category?.id;
    
    return {
      id: article.id,
      title: article.title,
      content: article.description || article.body || '',
      category: categoryName,
      folderName: folderName,
      folderId: folderId,
      author: 'Tourzone Support Team',
      publishedDate: article.created_at ? new Date(article.created_at).toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : i18n.language === 'ru' ? 'ru-RU' : 'en-US') : 'Unknown',
      lastUpdated: article.updated_at ? new Date(article.updated_at).toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : 'Unknown',
      views: article.hits || 0,
      helpfulCount: article.thumbs_up || 0,
      notHelpfulCount: article.thumbs_down || 0,
      tags: article.tags || [],
      status: article.status === 2 ? 'published' : 'draft',
      readingTime: `5 ${t('article.readingTime')}`
    };
  }, [article, categoryName, folderId, id, folderData]);



  // Slug oluşturma fonksiyonu
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Özel karakterleri kaldır
      .replace(/\s+/g, '-') // Boşlukları tire ile değiştir
      .replace(/-+/g, '-') // Birden fazla tireyi tek tireye çevir
      .trim();
  };

  // Aynı folder'daki makaleleri bul - folderData'dan al
  const sectionArticles = useMemo(() => {
    if (!folderData?.data?.articles || !Array.isArray(folderData.data.articles)) return [];
    
    return folderData.data.articles.slice(0, 10).map(article => ({
      id: article.id,
      title: article.title,
      slug: createSlug(article.title),
      current: article.id === parseInt(processedArticle?.id)
    }));
  }, [folderData, processedArticle?.id]);





  if (isArticleLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        background: '#f8f9fa'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isArticleError || !processedArticle) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        background: '#f8f9fa'
      }}>
        <h2>{t('article.articleNotFound')}</h2>
        <p>{t('article.articleNotFoundMessage')}</p>
        <Link to="/faq">
          <Button type="primary" size="large" style={{ marginTop: 16 }}>
            {t('article.backToFaq')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.articleContainer}>
      {/* Header Section with Gradient Background */}
      <div className={styles.headerSection}>
        <div className={styles.headerContent}>
          {/* Breadcrumb */}
          <Breadcrumb className={styles.breadcrumb}>
            <Breadcrumb.Item>
              <Link to="/">Tourzone Help Center</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/faq">FAQ</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/${i18n.language}/support/solutions/folders/${processedArticle.folderId}`}>
                {processedArticle.folderName}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{processedArticle.title}</Breadcrumb.Item>
          </Breadcrumb>

          <div className={styles.headerFlex}>
            <div className={styles.titleSection}>
              <Title level={1} className={styles.articleTitle}>
                {processedArticle.title}
              </Title>
              <Paragraph className={styles.updateInfo}>
                {t('article.updated')} {processedArticle.lastUpdated}
              </Paragraph>
            </div>
            
            <div className={styles.searchSection}>
              <div className={styles.searchContainer}>
                <SearchOutlined className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder={t('article.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const query = e.target.value.trim();
                      if (query) {
                        window.location.href = `/search?query=${encodeURIComponent(query)}`;
                      }
                    }
                  }}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          <div className={styles.contentGrid}>
            {/* Main Article Content */}
            <div>
              <Card className={styles.articleCard}>
                <div 
                  className={styles.articleContent}
                  dangerouslySetInnerHTML={{ __html: processedArticle.content }}
                />
              </Card>
            </div>

            {/* Right Sidebar */}
            <div className={styles.sidebar}>
              {/* Articles in this section */}
              <Card 
                title={t('article.articlesInSection')} 
                className={styles.sectionCard}
                headStyle={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  borderBottom: '1px solid #f0f0f0',
                  padding: '16px 20px'
                }}
                bodyStyle={{ padding: '0' }}
              >
                {!folderData ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Spin size="small" />
                  </div>
                ) : sectionArticles.length > 0 ? (
                  <List
                    dataSource={sectionArticles}
                    renderItem={(sectionArticle) => (
                      <List.Item className={`${styles.sectionItem} ${sectionArticle.current ? styles.current : ''}`}>
                        <Link 
                          to={`/${i18n.language}/support/solutions/articles/${sectionArticle.id}-${sectionArticle.slug || ''}`}
                          className={`${styles.sectionLink} ${sectionArticle.current ? styles.current : ''}`}
                        >
                          {sectionArticle.title}
                        </Link>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div className={styles.emptySection}>
                    {t('article.noOtherArticles')}
                  </div>
                )}
              </Card>
            </div>
          </div>



          {/* Quick Actions */}
          {/* Temporarily disabled
          <Card 
            title="Need Help?" 
            style={{ 
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none'
            }}
            headStyle={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              borderBottom: '1px solid #f0f0f0',
              padding: '16px 20px'
            }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Link to="/contact">
                <Button type="primary" block style={{ 
                  borderRadius: 6,
                  height: 40,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}>
                  Contact Support
                </Button>
              </Link>
            </div>
          </Card>
          */}
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail; 